import { images } from '@/asset';
import ButtonIconVertical from '@/components/ButtonIconVertical';
import ButtonImageVertical from '@/components/ButtonImageVertical';
import { CountryIcon, ZipcodeIcon } from '@/components/Icons';
import { REST_OF_THE_WORLD } from '@/constants';
import { BREAKPOINT, LocationConditionEnum } from '@/constants/enum';
import { unionCountries } from '@/constants/options';
import useUserPlan from '@/hooks/useUserPlan';
import checkoutRuleSlice from '@/redux/slice/checkoutRule.slice';
import ruleSlice, { IRuleErrorField } from '@/redux/slice/rule.slice';
import { IConditionValue, IItemCondition, ILocationCondition } from '@/types/newSetup.type';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import CityIcon from '../Icons/CityIcon';
import StateIcon from '../Icons/StateIcon';
interface IProps {
  setIsOpenModal?: (isOpenModal: boolean) => void;
  onAdd: (key: IItemCondition['type'] | ILocationCondition['type']) => (value: IConditionValue[] | null) => void;
  data: ILocationCondition[];
  isInCheckout?: boolean;
}

const SelectLocationCondition = ({ setIsOpenModal, onAdd, data, isInCheckout }: IProps) => {
  const dispatch = useDispatch();
  const { limitPlan, isPlanLimited } = useUserPlan();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.XS });

  const currentSlice = useMemo(() => {
    return isInCheckout ? checkoutRuleSlice : ruleSlice;
  }, [isInCheckout]);

  const listActions = useMemo(() => {
    const isSelectedCountry = !!data?.find((item) => item.type === LocationConditionEnum.Country);
    const isSelectedState = !!data?.find((item) => item.type === LocationConditionEnum.State);
    const isSelectedZipcode = !!data?.find((item) => item.type === LocationConditionEnum.Zipcode);
    const isSelectedCity = !!data?.find((item) => item.type === LocationConditionEnum.City);
    const dataCountry = data?.find((item) => item.type === LocationConditionEnum.Country);
    const isSelectedRegion = dataCountry?.value.some((item) =>
      [...Object.keys(unionCountries), '*', REST_OF_THE_WORLD].includes(item.data),
    );

    const handleAddCondition = (type: ILocationCondition['type']) => {
      onAdd(type)([{ data: '' }]);
      setIsOpenModal?.(false);
    };

    const country = {
      icon: isInCheckout ? <CountryIcon /> : <img width={'100%'} src={images.countryInBtn} alt="location" />,
      title: 'Regions, Countries',
      onClick: () => {
        handleAddCondition(LocationConditionEnum.Country);
        dispatch(
          currentSlice.actions.handleSetError({
            //@ts-ignore-next-line
            value: { field: 'country' as IRuleErrorField, msg: 'Please select at least 1 country' },
          }),
        );
      },
      disabled: isPlanLimited || isSelectedCountry || (!isInCheckout && isSelectedZipcode),
      tooltip: isPlanLimited
        ? 'Upgrade plan to use'
        : isSelectedCountry
        ? 'Condition was already added'
        : !isInCheckout && isSelectedZipcode
        ? "Cannot use 'Regions, countries' with 'Zipcodes' condition"
        : '',
    };

    const zipcode = {
      icon: isInCheckout ? <ZipcodeIcon /> : <img width={'100%'} src={images.zipcodeInBtn} alt="zipcode" />,
      title: 'Zipcode',
      onClick: () => {
        handleAddCondition(LocationConditionEnum.Zipcode);
        dispatch(
          currentSlice.actions.handleSetError({
            //@ts-ignore-next-line
            value: { field: 'zipcode' as IRuleErrorField, msg: 'Please enter at least 1 zipcode' },
          }),
        );
      },
      disabled: isPlanLimited || isSelectedZipcode || (!isInCheckout && isSelectedCountry),
      tooltip: isPlanLimited
        ? 'Upgrade plan to use'
        : isSelectedZipcode
        ? 'Condition was already added'
        : !isInCheckout && isSelectedCountry
        ? "Cannot use 'Zipcodes' with 'Regions, countries' condition"
        : '',
    };

    const state = {
      icon: isInCheckout ? <StateIcon /> : <img width={'100%'} src={images.regionInBtn} alt="state" />,
      title: 'States/Provinces' + (isInCheckout ? '/Counties' : ''),
      onClick: () => {
        handleAddCondition(LocationConditionEnum.State);
        dispatch(
          currentSlice.actions.handleSetError({
            //@ts-ignore-next-line
            value: { field: 'state' as IRuleErrorField, msg: 'Please select at least 1 state/province' },
          }),
        );
      },
      disabled:
        isPlanLimited ||
        isSelectedState ||
        !isSelectedCountry ||
        dataCountry?.value?.filter((item) => item.data)?.length !== 1 ||
        isSelectedRegion ||
        (limitPlan && !limitPlan?.canAddState),
      tooltip:
        (limitPlan && !limitPlan?.canAddState) || isPlanLimited
          ? 'Upgrade plan to use'
          : dataCountry?.value?.filter((item) => item.data)?.length !== 1 || !isSelectedCountry || isSelectedRegion
          ? 'Select only 1 country to enable'
          : 'This condition is already added',
    };

    const city = {
      icon: <CityIcon />,
      title: 'City',
      onClick: () => {
        handleAddCondition(LocationConditionEnum.City);
        dispatch(
          currentSlice.actions.handleSetError({
            //@ts-ignore-next-line
            value: { field: 'city' as IRuleErrorField, msg: 'Please enter at least 1 city' },
          }),
        );
      },
      disabled: isSelectedCity,
      tooltip: 'This condition is already added',
    };

    return isInCheckout ? [country, zipcode, city, state].filter((item) => !!item) : [country, zipcode, state];
  }, [data, isInCheckout, isPlanLimited, limitPlan, onAdd, setIsOpenModal, dispatch, currentSlice.actions]);

  return (
    <div className={`d-flex align-center gap-8 w-full ${isMobile ? 'flex-column' : 'flex-row'}`}>
      {listActions
        .filter((i) => !!i)
        .map((item, index) => (
          <div key={index} className="w-full">
            {isInCheckout ? (
              <ButtonIconVertical
                icon={item.icon}
                children={item.title}
                onClick={item.onClick}
                disabled={item.disabled}
                tooltip={item.disabled && item.tooltip}
              />
            ) : (
              <ButtonImageVertical
                icon={item.icon}
                title={item.title}
                onClick={item.onClick}
                disabled={item.disabled}
                tooltip={!!item.disabled && item.tooltip}
                padding="0"
              />
            )}
          </div>
        ))}
    </div>
  );
};

export default SelectLocationCondition;
